<template>
  <div class="login-form">
    <!-- Error Alert -->
    <div v-if="hasError" class="alert alert-danger" role="alert">
      <i class="fas fa-exclamation-triangle"></i>
      {{ errorMessage }}
    </div>

    <!-- Success Alert -->
    <div v-if="successMessage" class="alert alert-success" role="alert">
      <i class="fas fa-check-circle"></i>
      {{ successMessage }}
    </div>

    <form @submit.prevent="handleLogin" class="auth-form">
      <div class="form-group">
        <input
          id="email"
          v-model="credentials.email"
          type="email"
          class="form-control"
          :class="{ 'is-invalid': errors.email }"
          placeholder="Enter your email"
          required
          data-test="email"
          :disabled="isLoading"
        />
        <div v-if="errors.email" class="invalid-feedback">
          {{ errors.email[0] }}
        </div>
      </div>

      <div class="form-group">
        <div class="password-input-wrapper">
          <input
            id="password"
            v-model="credentials.password"
            :type="showPassword ? 'text' : 'password'"
            class="form-control"
            :class="{ 'is-invalid': errors.password }"
            placeholder="Enter your password"
            required
            data-test="password"
            :disabled="isLoading"
          />
          <button
            type="button"
            class="password-toggle"
            @click="showPassword = !showPassword"
            :disabled="isLoading"
          >
            <i :class="showPassword ? 'fi fi-eye-slash' : 'fi fi-eye'"></i>
          </button>
        </div>
        <div v-if="errors.password" class="invalid-feedback">
          {{ errors.password[0] }}
        </div>
      </div>

      <div class="form-options">
        <label class="checkbox-wrapper">
          <input
            v-model="rememberMe"
            type="checkbox"
            class="form-checkbox"
            :disabled="isLoading"
          />
          <span class="checkmark"></span>
          Remember me
        </label>

        <router-link
          :to="`/${currentLocale}/auth/forgot-password`"
          class="forgot-link"
        >
          Forgot Password?
        </router-link>
      </div>

      <button
        type="submit"
        class="btn btn-primary btn-block"
        :disabled="isLoading"
        data-test="login-button"
      >
        <span v-if="isLoading" class="loading-spinner"></span>
        {{ isLoading ? "Signing In..." : "Sign In" }}
      </button>
    </form>

    <div class="form-footer">
      <p class="signup-link">
        Don't have an account?
        <router-link
          :to="`/${currentLocale}/auth/register`"
          class="link-primary"
        >
          Sign up here
        </router-link>
      </p>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import AuthUtils from "../../utils/auth.utils";
import { SessionService } from "../../services/session.service";

export default {
  name: "Login",
  data() {
    return {
      credentials: {
        email: "",
        password: "",
      },
      rememberMe: false,
      showPassword: false,
      errors: {},
      successMessage: "",
      currentLocale: AuthUtils.getCurrentLocale(),
    };
  },

  computed: {
    ...mapGetters(["isLoading", "hasError", "getError"]),

    isLoading() {
      return this.$store.getters.isLoading;
    },

    hasError() {
      return this.$store.getters.hasError;
    },

    errorMessage() {
      const storeError = this.$store.getters.getError;
      if (storeError) return storeError;

      // Handle validation errors
      if (Object.keys(this.errors).length > 0) {
        const firstError = Object.values(this.errors)[0];
        return Array.isArray(firstError) ? firstError[0] : firstError;
      }

      return "";
    },
  },

  methods: {
    ...mapActions(["apiLogin"]),

    async clearRateLimit() {
      // Only available in development/testing
      if (process.env.NODE_ENV === "development") {
        try {
          await this.$http.post("/api/auth/clear-rate-limit", {
            email: this.credentials.email,
          });
        } catch (error) {
          console.log("Rate limit clear not available or failed");
        }
      }
    },

    async handleLogin() {
      // Clear previous errors
      this.errors = {};
      this.successMessage = "";

      // Basic validation
      if (!this.credentials.email || !this.credentials.password) {
        this.errors = {
          email: this.credentials.email ? [] : ["Email is required"],
          password: this.credentials.password ? [] : ["Password is required"],
        };
        return;
      }

      try {
        await this.$store.dispatch("apiLogin", this.credentials);

        this.successMessage = "Login successful! Redirecting...";

        // Initialize session management after successful login
        SessionService.init();

        // Use AuthUtils for redirect handling
        setTimeout(() => {
          const redirectPath = this.$route.query.redirect;
          AuthUtils.redirectAfterLogin(redirectPath);
        }, 1000);
      } catch (error) {
        // Handle specific error types
        if (error.status === 422 && error.errors) {
          this.errors = error.errors;
        } else if (error.status === 429) {
          // Rate limiting - try to clear rate limit in development
          if (process.env.NODE_ENV === "development") {
            console.log("Rate limit hit - attempting to clear for development");
            await this.clearRateLimit();
          }
          // Rate limiting handled by store, but add user-friendly message
          this.errors = {
            general: [
              "Too many login attempts. Please wait a moment and try again.",
            ],
          };
        } else {
          // Generic error handling done by store
        }
      }
    },
  },

  created() {
    // Check if user is already authenticated
    if (AuthUtils.isAuthenticated()) {
      AuthUtils.redirectAfterLogin();
      return;
    }

    // Check for any success messages from registration
    if (this.$route.query.registered === "true") {
      this.successMessage = "Registration successful! Please sign in.";
    }
  },
};
</script>

<style scoped>
.login-form {
  width: 100%;
}

.form-title {
  color: #333;
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  text-align: center;
}

.form-subtitle {
  color: #666;
  font-size: 0.9rem;
  text-align: center;
  margin-bottom: 2rem;
}

.alert {
  padding: 0.75rem 1rem;
  border-radius: 8px;
  margin-bottom: 1.5rem;
  border: none;
  font-size: 0.9rem;
}

.alert-danger {
  background-color: #fee;
  color: #c53030;
}

.alert-success {
  background-color: #f0fff4;
  color: #38a169;
}

.alert i {
  margin-right: 0.5rem;
}

.auth-form {
  width: 100%;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-label {
  display: block;
  margin-bottom: 0.5rem;
  color: #333;
  font-weight: 500;
  font-size: 0.9rem;
  text-align: left;
}

.form-control {
  width: 100%;
  padding: 0.75rem 1rem;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 1rem;
  transition: all 0.2s ease;
  background-color: #fff;
}

.form-control:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.form-control.is-invalid {
  border-color: #e53e3e;
}

.form-control:disabled {
  background-color: #f7fafc;
  cursor: not-allowed;
}

.password-input-wrapper {
  position: relative;
}

.password-toggle {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #666;
  cursor: pointer;
  padding: 0.25rem;
  transition: color 0.2s ease;
}

.password-toggle:hover {
  color: #333;
}

.password-toggle:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.invalid-feedback {
  color: #e53e3e;
  font-size: 0.85rem;
  margin-top: 0.25rem;
}

.form-options {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
}

.checkbox-wrapper {
  display: flex;
  align-items: center;
  cursor: pointer;
  font-size: 0.9rem;
  color: #666;
}

.form-checkbox {
  margin-right: 0.5rem;
}

.forgot-link {
  color: #667eea;
  text-decoration: none;
  font-size: 0.9rem;
  transition: color 0.2s ease;
}

.forgot-link:hover {
  color: #5a6fd8;
  text-decoration: underline;
}

.btn {
  padding: 0.875rem 1rem;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-primary {
  background: linear-gradient(135deg, #2c3e50 0%, #34495e 100%);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
}

.btn-primary:hover:not(:disabled) {
  background: linear-gradient(135deg, #34495e 0%, #2c3e50 100%);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
}

.btn-primary:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  transform: none;
  background: linear-gradient(135deg, #95a5a6 0%, #7f8c8d 100%);
}

.btn-block {
  width: 100%;
}

.loading-spinner {
  width: 18px;
  height: 18px;
  border: 2px solid transparent;
  border-top: 2px solid currentColor;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-right: 0.5rem;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.form-footer {
  text-align: center;
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid #eee;
}

.signup-link {
  color: #666;
  font-size: 0.9rem;
  margin: 0;
}

.link-primary {
  color: #2c3e50;
  text-decoration: none;
  font-weight: 500;
  transition: color 0.2s ease;
}

.link-primary:hover {
  color: #34495e;
  text-decoration: underline;
}

/* Responsive */
@media (max-width: 480px) {
  .form-options {
    flex-direction: column;
    gap: 1rem;
    align-items: flex-start;
  }

  .form-title {
    font-size: 1.3rem;
  }
}
</style>
